import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { normalizeMediaUrl } from "../lib/media";
import { formatDateSmart } from "../lib/time";
import api from "../api";
import PostCard from "../components/PostCard";
import "../theme/design.css";


/* ================= PROFILE PAGE ================= */
export default function Profile() {
  const { handle } = useParams();
  const normHandle = String(handle || "").toLowerCase();
  const navigate = useNavigate();

  const [me, setMe] = useState(null);
  const [profile, setProfile] = useState(null);
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [locked, setLocked] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(false);       // initial load
  const [followLoading, setFollowLoading] = useState(false);
const [loadingMore, setLoadingMore] = useState(false); // pagination ONLY
const [menuOpen, setMenuOpen] = useState(false);
const menuRef = useRef(null);

  const mountedRef = useRef(true);

const [showDeleteModal, setShowDeleteModal] = useState(false);
const [deletePassword, setDeletePassword] = useState("");

  /* ---------------- Load /me ---------------- */
  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/me");
      setMe(data?.user || null);
      if (data?.user) localStorage.setItem("user", JSON.stringify(data.user));
    } catch {
      setMe(null);
    }
  }, []);

// logout function 
const logout = () => {
  localStorage.clear();
  window.location.href = "/login";
};

  /* ---------------- Load profile header ---------------- */
  const loadHeader = useCallback(async () => {
    try {
      const { data } = await api.get(`/profiles/${normHandle}`);

const profileData = data.profile || {};
profileData.followers_count = data.stats?.followers || 0;
profileData.following_count = data.stats?.following || 0;

setProfile(profileData); setIsFollowing(data.you_follow || false);
    } catch (e) {
      console.error("profile load error", e?.response?.data || e.message);
      setProfile({ user_id: normHandle, username: normHandle });
    }
  }, [normHandle]);


// follow/unfollow functions
const followUser = async () => {
  if (followLoading) return;

  setFollowLoading(true);

  try {
    await api.post(`/profiles/${normHandle}/follow`);

    setIsFollowing(true);

    setProfile(prev => ({
      ...prev,
      followers_count: (prev.followers_count || 0) + 1
    }));

  } catch (e) {
    alert(e?.response?.data?.error || "Follow failed");
  } finally {
    setFollowLoading(false);
  }
};

const unfollowUser = async () => {
  if (followLoading) return;

  setFollowLoading(true);

  try {
    await api.delete(`/profiles/${normHandle}/follow`);

    setIsFollowing(false);

    setProfile(prev => ({
      ...prev,
      followers_count: Math.max((prev.followers_count || 1) - 1, 0)
    }));

  } catch (e) {
    alert(e?.response?.data?.error || "Unfollow failed");
  } finally {
    setFollowLoading(false);
  }
};

  /* ---------------- Load profile feed ---------------- */
  const loadFeed = useCallback(
  async (reset = false) => {
    if (reset) {
      if (loading) return;
      setLoading(true);
    } else {
      if (loadingMore) return;
      setLoadingMore(true);
    }

    try {
      const qs = new URLSearchParams();
      qs.set("limit", "20");

      if (!reset && cursor) {
        qs.set("cursor", cursor);
      }

      const { data } = await api.get(
        `/profiles/${normHandle}/feed?${qs}`
      );

      if (data?.locked) {
  setLocked(true);
  setRows([]);
  setCursor(null);
  return;
}

setLocked(false);

const list = data?.posts || [];
setRows(prev => (reset ? list : [...prev, ...list]));
setCursor(data?.nextCursor || null);
    } catch (e) {
      console.error("feed load error", e?.response?.data || e.message);
    } finally {
      if (reset) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  },
  [normHandle, cursor, loading, loadingMore]
);

//Delete Account 
const deleteAccount = async () => {

  if (!deletePassword) {
    alert("Enter your password");
    return;
  }

  const confirmDelete = window.confirm(
    "Final warning: Delete your account permanently?"
  );

  if (!confirmDelete) return;

  try {

    await api.delete("/account", {
      data: { password: deletePassword }
    });

    localStorage.clear();

    alert("Your account has been permanently deleted.");

    window.location.href = "/";

  } catch (e) {

    alert(e?.response?.data?.error || "Delete failed");

  }

};

  /* ---------------- Init ---------------- */
  useEffect(() => {
  mountedRef.current = true;

  (async () => {
    setCursor(null);        // reset pagination
    setRows([]);            // reset posts
    await loadMe();
    await loadHeader();
    await loadFeed(true);   // ONLY initial load
  })();

  return () => {
    mountedRef.current = false;
  };
}, [normHandle]); // 👈 ONLY handle change

/* ---------------- Close dropdown when clicking outside ---------------- */
useEffect(() => {
  const handler = (e) => {
    if (menuRef.current && !menuRef.current.contains(e.target)) {
      setMenuOpen(false);
    }
  };

  document.addEventListener("mousedown", handler);
  return () => document.removeEventListener("mousedown", handler);
}, []);

  const isOwnProfile =
    me?.user_id &&
    profile?.user_id &&
    String(me.user_id).toLowerCase() === String(profile.user_id).toLowerCase();

  const followersCount = profile?.followers_count ?? 0;
  const followingCount = profile?.following_count ?? 0;

  if (!profile) return <div className="page">Loading…</div>;

  /* ---------------- Actions ---------------- */
  const deleteRepost = async (post) => {
  if (!post.repost_id) return;

  if (!window.confirm("Delete this repost?")) return;

  try {
    await api.delete(`/reposts/${post.repost_id}`);

    // remove repost from UI immediately
    setRows(prev => prev.filter(p => p.repost_id !== post.repost_id));
  } catch (e) {
    alert(e?.response?.data?.error || "Failed to delete repost");
  }
};
  
  const handleDeletePost = async (post) => {
  const ok = window.confirm("Delete this post permanently?");
  if (!ok) return;

  // optimistic remove
  setRows(prev => prev.filter(p => p.id !== post.id));

  try {
    await api.delete(`/posts/${post.id}`);
  } catch (e) {
    alert("Delete failed");
    loadFeed(true); // fallback reload
  }
};

const handleChangePrivacy = async (post, vis) => {
  if (!["public", "friends", "private"].includes(vis)) return;

  // ✅ optimistic update FIRST
  setRows(prev =>
    prev.map(p =>
      p.id === post.id ? { ...p, visibility: vis } : p
    )
  );

  try {
    await api.put(`/posts/${post.id}/visibility`, { visibility: vis });
  } catch (e) {
    alert("Privacy update failed");

    // rollback on failure
    setRows(prev =>
      prev.map(p =>
        p.id === post.id ? { ...p, visibility: post.visibility } : p
      )
    );
  }
};

  const toggleSupport = async (post) => {

  const endpoint = `/posts/${post.id}/support`;

  try {

    const { data } = await api.post(endpoint);
    const supported = data?.supported;

    setRows(prev =>
      prev.map(p => {

        const match =
          (post.is_repost && p.repost_id === post.repost_id) ||
          (!post.is_repost && p.id === post.id);

        if (!match) return p;

        return {
          ...p,
          is_supported: supported,
          support_count: supported
            ? (p.support_count || 0) + 1
            : Math.max((p.support_count || 1) - 1, 0)
        };

      })
    );

  } catch (e) {
    console.error("support toggle failed", e);
  }

};


  const doRepost = async (postId) => {
  try {
    await api.post(`/posts/${postId}/repost`);
    alert("Reposted to your profile");
  } catch (e) {
    alert(e?.response?.data?.error || "Repost failed");
  }
};

  const shareExternally = async (postId) => {
    const url = `${window.location.origin}/post/${postId}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Varta", url });
      } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      alert("Link copied!");
    }
  };

  /* ---------------- Render ---------------- */
  return (
    <div className="page">
      {/* Profile Header */}
      <div className="post-card" style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <img
            src={
              normalizeMediaUrl(profile.profile_pic_url) ||
              "/uploads/default-avatar.png"
            }
            alt=""
            style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover" }}
          />
           
           
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 700 }}>
              {profile.username || profile.user_id}
            </div>
            <div style={{ color: "#6B7280" }}>@{profile.user_id}</div>
            {profile.bio && <div style={{ marginTop: 6 }}>{profile.bio}</div>}

            <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
              <span
  style={{ cursor: "pointer", color: "#93c5fd" }}
  onClick={() => navigate(`/u/${profile.user_id}/follows?tab=followers`)}
>
  <strong>{followersCount}</strong> Followers
</span>

<span
  style={{ cursor: "pointer", color: "#93c5fd" }}
  onClick={() => navigate(`/u/${profile.user_id}/follows?tab=following`)}
>
  <strong>{followingCount}</strong> Following
</span>
            </div>
          </div>

   {/* Follow button (for other users) */}
{!isOwnProfile && me && (
  <div>
    {isFollowing ? (
      <button
  className="btn btn-secondary"
  onClick={unfollowUser}
  disabled={followLoading}
>
  Following
</button>
    ) : (
      <button
  className="btn btn-primary"
  onClick={followUser}
  disabled={followLoading}
>
  {followLoading ? "Following..." : "Follow"}
</button>
    )}
  </div>
)}

{/* Account menu (for own profile) */}
{isOwnProfile && (
  <div ref={menuRef} style={{ position: "relative" }}>
    <button
      className="btn btn-primary"
      onClick={() => setMenuOpen(!menuOpen)}
    >
      Account ▾
    </button>

    {menuOpen && (
      <div
        style={{
          position: "absolute",
          right: 0,
          top: 40,
          background: "#111827",
          borderRadius: 8,
          padding: 10,
          width: 180,
          boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
          display: "flex",
          flexDirection: "column",
          gap: 8
        }}
      >
        <Link
  to="/edit-profile"
  style={{ padding: "8px 6px", fontSize: 15 }}
>
  Edit profile
</Link>

<Link
  to="/privacy"
  onClick={() => setMenuOpen(false)}
  style={{ padding: "8px 6px", fontSize: 15 }}
>
  Privacy
</Link>

<Link
  to="/change-password"
  style={{ padding: "8px 6px", fontSize: 15 }}
>
  Change password
</Link>

<Link
  to="/supported"
  style={{ padding: "8px 6px", fontSize: 15 }}
>
  Supported posts
</Link>

<button
  onClick={logout}
  style={{
    textAlign: "left",
    background: "none",
    border: "none",
    color: "#fff",
    padding: "8px 6px",
    fontSize: 15,
    cursor: "pointer"
  }}
>
  Logout
</button>

<button
  onClick={() => setShowDeleteModal(true)}
  style={{
    textAlign: "left",
    background: "none",
    border: "none",
    color: "#ef4444",
    padding: "8px 6px",
    fontSize: 15,
    cursor: "pointer"
  }}
>
  Delete account
</button>
      </div>
    )}
  </div>
)}
        </div>
      </div>

{showDeleteModal && (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.6)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000
    }}
  >
    <div
      style={{
        background: "#111827",
        padding: 24,
        borderRadius: 12,
        width: 320
      }}
    >
      <h3 style={{ marginBottom: 10 }}>Delete account</h3>

      <p style={{ fontSize: 14, color: "#9ca3af" }}>
        This action is permanent. Enter your password to continue.
      </p>

      <input
        type="password"
        placeholder="Enter password"
        value={deletePassword}
        onChange={(e) => setDeletePassword(e.target.value)}
        style={{
          width: "100%",
          padding: 10,
          marginTop: 12,
          borderRadius: 6,
          border: "1px solid #374151",
          background: "#000",
          color: "#fff"
        }}
      />

      <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
        <button
          className="btn btn-secondary"
          onClick={() => setShowDeleteModal(false)}
        >
          Cancel
        </button>

        <button
  className="btn"
  style={{ background: "#ef4444", color: "#fff" }}
  disabled={!deletePassword}
  onClick={deleteAccount}
>
  Delete
</button>
      </div>
    </div>
  </div>
)}

      {/* Private Account */}
{locked && !isOwnProfile && (
  <div
    className="post-card"
    style={{
      textAlign: "center",
      padding: 40,
      fontSize: 16
    }}
  >
    🔒 This account is private
    <div style={{ marginTop: 6, color: "#6B7280" }}>
      Follow this user to see their posts
    </div>
  </div>
)}

{/* Feed */}
{!locked && (
  <>
    <div className="feed-col">
      {rows.map((p) => (
  <PostCard
    key={p.repost_id || p.id}
    p={{
      ...p,
      _displayDate: formatDateSmart(
        p.reposted_at || p.sort_ts
      ),
    }}
    isOwnProfile={isOwnProfile}
    onSupport={toggleSupport}
    onRepost={doRepost}
    onShare={shareExternally}
    onDelete={handleDeletePost}
    onDeleteRepost={deleteRepost}
    onChangePrivacy={handleChangePrivacy}
  />
))}
    </div>

    <div className="pager">
      {cursor && !loading && (
        <button
          className="btn btn-primary"
          disabled={loadingMore}
          onClick={() => loadFeed(false)}
        >
          {loadingMore ? "Loading…" : "Load more"}
        </button>
      )}
    </div>
</>
)}
    </div>
  );
}